import { useEffect, useMemo, useState } from 'react';
import { Trash2, X } from 'lucide-react';
import type { CommandAck, CommandAckStatus, RobotEvent } from '@fabricos/protocol-ts';
import { Badge } from '@/components/ui/Badge';
import { PopOutButton } from '@/components/layout/PopOutButton';
import { armConfirm } from '@/lib/armConfirm';
import { eventSeverityColor, eventSummary, fmtClock } from '@/lib/events';
import { sendGoto } from '@/lib/gotoSend';
import { useCommandLog } from '@/store/commandLog';
import { useFleet } from '@/store/fleet';
import { useFlightStats, flightTimeMs, fmtFlightTime } from '@/store/flightStats';
import { getDistanceUnit, useSettings } from '@/store/settings';
import { useGotoTargets, distMeters } from '@/store/gotoTargets';
import { useMapMode } from '@/store/mapMode';
import { useSelection } from '@/store/selection';
import { useToasts } from '@/store/toasts';
import { fmtDistance, fmtMeters, fmtNum } from '@/lib/utils';
import { cn } from '@/lib/utils';
import { formatFlightMode } from '@/lib/flightMode';

const SHELL_W = 'var(--rail-right)';

export function TelemetryPanel({
  open = false,
  onClose,
}: {
  /** Drawer mode (sub-`lg`) — controls visibility. Ignored at `lg`+. */
  open?: boolean;
  onClose?: () => void;
} = {}) {
  const robots = useFleet((s) => s.robots);
  const states = useFleet((s) => s.states);
  const acks = useFleet((s) => s.acks);
  const events = useFleet((s) => s.events);
  const targets = useGotoTargets((s) => s.targets);
  const history = useGotoTargets((s) => s.history);
  const selected = useSelection((s) => s.selected);
  const removeRobot = useFleet((s) => s.removeRobot);
  const clearSel = useSelection((s) => s.clear);
  // Hooks must run unconditionally — the `if (!focusedId) return null` below
  // is an early return, so these CANNOT live after it.
  const sessions = useFlightStats((s) => s.sessions);
  const distUnit = useSettings((s) => getDistanceUnit(s));
  const [battShowVolts, setBattShowVolts] = useState(false);
  const [gpsShowFix, setGpsShowFix] = useState(false);

  useEffect(() => {
    if (!open || !onClose) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose!();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Selection-only: the panel is mounted by the route only when a drone is
  // selected, so no fallback to "first robot".
  const focusedId = useMemo(() => {
    if (selected.size === 0) return null;
    return selected.values().next().value as string;
  }, [selected]);

  const shellClass = cn(
    'flex h-full flex-none flex-col border-l border-border-default bg-surface-1',
    'fixed inset-y-0 right-0 z-40 transition-transform duration-200 ease-out',
    'top-[var(--rail-topbar)]',
    open ? 'visible translate-x-0' : 'invisible translate-x-full',
    'lg:static lg:visible lg:translate-x-0 lg:top-0',
  );

  const backdrop = (
    <div
      onClick={onClose}
      aria-hidden
      className={cn(
        'fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] transition-opacity lg:hidden',  
        open ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
    />
  );

  const closeBtn = onClose && (
    <button
      onClick={onClose}
      title="Close telemetry panel"
      aria-label="Close"
      className="grid h-6 w-6 place-items-center rounded text-fg-secondary hover:bg-surface-2 hover:text-fg-primary lg:hidden"
    >
      <X className="h-3.5 w-3.5" />
    </button>
  );

  if (!focusedId) return null;

  const robot = robots[focusedId];
  const state = states[focusedId];
  const statusTone = state?.connection === 'lost'
    ? 'critical'
    : state?.connection === 'degraded'
      ? 'warn'
      : state?.arm === 'armed'
        ? 'inflight'
        : 'good';

  const altRel = state?.position?.alt_rel_home_m ?? 0;
  const gndSpd = state?.velocity?.ground_speed_ms ?? 0;
  const yawDeg = state?.attitude ? ((state.attitude.yaw_rad * 180) / Math.PI + 360) % 360 : null;
  const batt = state?.battery?.remaining_pct ?? null;
  const flight = sessions[focusedId];

  return (
    <>
      {backdrop}
      <aside className={shellClass} style={{ width: SHELL_W }}>
      {/* Bracketed callsign header */}
      <header className="border-b border-border-default px-4 py-3">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span className="font-mono text-[17px] leading-none text-accent">[</span>
              <h2 className="m-0 truncate font-mono text-[17px] leading-none tracking-[0.02em] text-fg-primary">
                {robot?.name ?? focusedId}
              </h2>
              <span className="font-mono text-[17px] leading-none text-accent">]</span>
            </div>
            <p className="mt-1 truncate font-mono text-[10px] uppercase tracking-[0.08em] text-fg-muted">
              {(robot?.kind.frame as any)?.platform ?? 'craft'} / {formatFlightMode(state?.mode)}
            </p>
          </div>
          <div className="flex flex-none items-center gap-1">
            <Badge tone={statusTone}>
              {state?.arm === 'armed' ? 'ARMED' : state?.connection ?? 'unknown'}
            </Badge>
            <PopOutButton
              solo="telemetry"
              robotId={focusedId}
              title={`Telemetry — ${robot?.name ?? focusedId}`}
            />
            <button
              title="Deregister asset"
              onClick={async () => {
                if (!armConfirm(`deregister-${focusedId}`, `Deregister ${robot?.name ?? focusedId}?`)) return;
                const { authHeader } = await import('@/store/auth');
                const { hubFetch } = await import('@/lib/hubUrl');
                await hubFetch(`/robots/${focusedId}`, {
                  method: 'DELETE',
                  headers: { ...authHeader() },
                });
                removeRobot(focusedId);
                clearSel();
              }}
              className="grid h-6 w-6 place-items-center rounded text-status-critical/70 hover:bg-surface-2 hover:text-status-critical"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
            {closeBtn}
          </div>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 space-y-5">
        {/* 3×2 glance grid — the HUD signature */}
        <div
          className="grid grid-cols-3 overflow-hidden rounded border border-border-default"
          style={{ background: 'var(--color-border-subtle)', gap: '1px' }}
        >
          <GlanceCell label="ALT REL" value={fmtNum(altRel, 1)} unit="M" />
          <GlanceCell label="GND SPD" value={fmtNum(gndSpd, 1)} unit="M/S" />
          <GlanceCell label="VERT SPD" value={fmtNum(state?.velocity?.vz_ms, 1)} unit="M/S" />
          <GlanceCell label="HEADING" value={yawDeg != null ? String(Math.round(yawDeg)).padStart(3, '0') : '—'} unit="°" />
          <GlanceCell
            label="BATTERY"
            value={
              battShowVolts
                ? fmtNum(state?.battery?.voltage_v, 2)
                : batt != null
                  ? String(Math.round(batt))
                  : '—'
            }
            unit={battShowVolts ? 'V' : '%'}
            tone={!battShowVolts && batt != null && batt < 25 ? 'warn' : 'default'}
            onClick={() => setBattShowVolts((v) => !v)}
            title="Click to toggle percentage ↔ voltage"
          />
          <GlanceCell
            label={gpsShowFix ? 'GPS FIX' : 'SATS'}
            value={
              gpsShowFix
                ? (state?.gps?.fix ?? 'no gps')
                : state?.gps?.satellites != null
                  ? String(state.gps.satellites)
                  : '—'
            }
            unit=""
            tone={
              gpsShowFix && (state?.gps?.fix === 'none' || state?.gps?.fix === 'fix2d' || !state?.gps)
                ? 'warn'
                : 'default'
            }
            onClick={() => setGpsShowFix((v) => !v)}
            title="Click to toggle satellite count ↔ fix type"
          />
          {/* Flight session totals — tick while armed, freeze after disarm. */}
          <GlanceCell
            label="FLT TIME"
            value={flight ? fmtFlightTime(flightTimeMs(flight)) : '—'}
            unit=""
          />
          <GlanceCell
            label="FLT DIST"
            value={flight ? fmtDistance(flight.distanceM, distUnit) : '—'}
            unit=""
          />
          {/* Straight-line displacement from home (firmware home; falls
              back to this session's takeoff position). */}
          <GlanceCell
            label="HOME DIST"
            value={(() => {
              const ref = state?.home ?? flight?.takeoffPos;
              return ref && state?.position
                ? fmtDistance(distMeters(state.position, ref), distUnit)
                : '—';
            })()}
            unit=""
          />
        </div>

        {/* "GOTO active" box removed 2026-07-14 (redundancy audit): the
            on-map line label already shows target + live distance. */}
        {(() => {
          const items = history[focusedId] ?? [];
          if (items.length === 0) return null;
          return (
            <div>
              <div className="mb-1.5 flex items-center gap-2">
                <span
                  className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em]"
                  style={{ color: 'var(--color-accent)' }}
                >
                  GOTO history
                </span>
                <span className="text-[9px] uppercase tracking-wider text-fg-muted">
                  click to re-send
                </span>
              </div>
              <div className="flex flex-wrap gap-1">
                {items.map((h, i) => (
                  <button
                    key={`${h.ts_ms}-${i}`}
                    type="button"
                    onClick={() => {
                      // Commanding stays gated on GOTO mode — same discipline
                      // as map clicks (operator call 2026-07-14).
                      if (useMapMode.getState().mode !== 'direct') {
                        useToasts.getState().push({
                          tone: 'warn',
                          title: 'Switch to GOTO mode to re-send',
                          body: 'Ctrl+Shift+M or click the mode strip, then click the history entry again.',
                          durationMs: 5000,
                        });
                        return;
                      }
                      // Fly at the altitude that leg used before — and show
                      // it in the dock's Alt field so it can be changed with
                      // Alt + GO afterwards.
                      useGotoTargets
                        .getState()
                        .setSettings({ defaultAltAglM: Math.round(h.alt_m) });
                      sendGoto(focusedId, h.lat, h.lon, {
                        altOverrideM: h.alt_m,
                        skipConfirm: true,
                      });
                    }}
                    title={`Re-send: ${h.lat.toFixed(5)}, ${h.lon.toFixed(5)} @ ${h.alt_m} m (needs GOTO mode; alt appears in the dock)`}
                    className="rounded border border-border-subtle bg-surface-2 px-1.5 py-0.5 font-mono text-[10px] text-fg-secondary hover:border-amber-500/60 hover:text-amber-300"
                  >
                    {h.lat.toFixed(3)},{h.lon.toFixed(3)} · {Math.round(h.alt_m)}m
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        <DataSection title="Position">
          <DataRow label="LAT" value={state?.position ? state.position.lat.toFixed(5) : '—'} />
          <DataRow label="LON" value={state?.position ? state.position.lon.toFixed(5) : '—'} />
          <DataRow label="ALT MSL" value={fmtNum(state?.position?.alt_msl_m, 1)} unit="M" />
          <DataRow label="ALT AGL" value={fmtNum(state?.position?.alt_agl_m, 1)} unit="M" />
        </DataSection>

        <DataSection title="Attitude">
          <DataRow label="ROLL" value={state?.attitude ? fmtNum((state.attitude.roll_rad * 180) / Math.PI, 1) : '—'} unit="°" />
          <DataRow label="PITCH" value={state?.attitude ? fmtNum((state.attitude.pitch_rad * 180) / Math.PI, 1) : '—'} unit="°" />
          <DataRow label="VERT SPD" value={fmtNum(state?.velocity?.vz_ms, 1)} unit="M/S" />
        </DataSection>

        {/* Power · Nav section removed 2026-07-14 (operator): voltage lives
            in the BATTERY glance cell (click it to toggle % ↔ V); GPS fix
            rides SATS; link state is the header badge. */}

        <ResultsFeed robotId={focusedId} acks={acks} />
        <EventsFeed robotId={focusedId} events={events} />

      </div>
      </aside>
    </>
  );
}

function GlanceCell({
  label,
  value,
  unit,
  tone = 'default',
  onClick,
  title,
}: {
  label: string;
  value: string;
  unit: string;
  tone?: 'default' | 'warn' | 'critical' | 'good';
  onClick?: () => void;
  title?: string;
}) {
  const colour =
    tone === 'warn' ? 'var(--color-status-warn)' :
    tone === 'critical' ? 'var(--color-status-critical)' :
    tone === 'good' ? 'var(--color-status-good)' :
    'var(--color-fg-primary)';
  return (
    <div
      className={cn('bg-surface-1 px-3 py-2.5', onClick && 'cursor-pointer hover:bg-surface-2')}
      onClick={onClick}
      title={title}
    >
      <div className="font-mono text-[9px] uppercase tracking-[0.16em] text-fg-muted">{label}</div>
      <div className="mt-0.5 flex items-baseline gap-1 font-mono text-[20px] leading-none tabular-nums" style={{ color: colour }}>
        {value}
        <span className="text-[11px] text-fg-muted">{unit}</span>
      </div>
    </div>
  );
}

function DataSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 flex items-center gap-2">
        <span
          className="font-mono text-[10px] font-semibold uppercase tracking-[0.16em]"
          style={{ color: 'var(--color-accent)' }}
        >
          {title}
        </span>
        <span className="h-px flex-1" style={{ background: 'var(--color-accent-edge)' }} />
      </div>
      <div className="divide-y divide-border-subtle/60">{children}</div>
    </div>
  );
}

function DataRow({
  label,
  value,
  unit,
  tone = 'default',
}: {
  label: string;
  value: string | number | null | undefined;
  unit?: string;
  tone?: 'default' | 'warn' | 'critical' | 'good';
}) {
  const colour =
    tone === 'warn' ? 'var(--color-status-warn)' :
    tone === 'critical' ? 'var(--color-status-critical)' :
    tone === 'good' ? 'var(--color-status-good)' :
    'var(--color-fg-primary)';
  const display = value == null ? '—' : String(value);
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 font-mono text-[11px]">
      <span className="whitespace-nowrap tracking-[0.04em] text-fg-muted">{label}</span>
      <span className="whitespace-nowrap tabular-nums" style={{ color: colour }}>
        {display}
        {unit && <span className="ml-1 text-fg-muted">{unit}</span>}
      </span>
    </div>
  );
}

const RESULTS_SHOWN = 8;
const EVENTS_SHOWN = 10;

const ACK_TONE: Record<CommandAckStatus, string> = {
  accepted: 'var(--color-status-good)',
  completed: 'var(--color-status-good)',
  in_progress: 'var(--color-fg-secondary)',
  rejected: 'var(--color-status-critical)',
  failed: 'var(--color-status-critical)',
  timeout: 'var(--color-status-warn)',
  cancelled: 'var(--color-status-warn)',
};

/** Command results — what the drone said back to each command we sent. */
function ResultsFeed({ robotId, acks }: { robotId: string; acks: CommandAck[] }) {
  const kinds = useCommandLog((s) => s.kinds);
  const mine = acks.filter((a) => a.robot_id === robotId).slice(0, RESULTS_SHOWN);
  if (mine.length === 0) return null;
  return (
    <DataSection title="Results">
      <div className="space-y-1 py-1">
        {mine.map((a) => (
          <div
            key={`${a.command_id}-${a.status}-${a.ts_ms}`}
            className="flex items-baseline gap-2 font-mono text-[10.5px]"
            title={a.message ?? undefined}
          >
            <span className="flex-none tabular-nums text-fg-muted">{fmtClock(a.ts_ms)}</span>
            <span className="truncate text-fg-secondary">
              {kinds[a.command_id] ?? `…${a.command_id.slice(-6)}`}
            </span>
            <span
              className="ml-auto flex-none uppercase tracking-[0.08em]"
              style={{ color: ACK_TONE[a.status] }}
            >
              {a.status}
            </span>
          </div>
        ))}
      </div>
    </DataSection>
  );
}

/** Aftermath — recent drone events, newest first. */
function EventsFeed({ robotId, events }: { robotId: string; events: RobotEvent[] }) {
  const mine = events
    .filter((e) => e.robot_id === robotId && e.kind !== 'param_value')
    .slice(0, EVENTS_SHOWN);
  if (mine.length === 0) return null;
  return (
    <DataSection title="Events">
      <div className="space-y-1 py-1">
        {mine.map((e, i) => (
          <div
            key={`${e.ts_ms}-${e.kind}-${i}`}
            className="flex items-baseline gap-2 font-mono text-[10.5px]"
          >
            <span className="flex-none tabular-nums text-fg-muted">{fmtClock(e.ts_ms)}</span>
            <span className="truncate" style={{ color: eventSeverityColor(e) }}>
              {eventSummary(e)}
            </span>
          </div>
        ))}
      </div>
    </DataSection>
  );
}

